import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DEFAULT_LANGUAGE, LANGUAGES, money, storeLanguage, storedLanguage, translator } from "../i18n";
import { useAuth } from "./AuthContext";
import { mapsLanguageIsStale, someMapIsMounted } from "../lib/mapsLanguage";

/**
 * Which language the app is rendered in.
 *
 * Deliberately shaped unlike {@link ThemeContext}, and the difference is the whole design. The
 * theme is a device setting — it lives in localStorage and nothing server-side has an opinion. The
 * language is an *account* setting, because the backend has to know it when the user is nowhere
 * near a browser: the daily digest sweeps sleeping users at 03:00 and renders their email from
 * `User.language` alone.
 *
 * So the server holds the truth and localStorage holds a copy. The copy exists for two things that
 * cannot wait for a round trip — the first paint after a reload, and `client.js`, which has to
 * stamp `Accept-Language` on requests that fire before `/me` has answered. Reconciliation is
 * one-directional and stated once, below.
 */
/*
 * `lang` is both the catalogue selector and the BCP-47 tag handed to `Intl` — one value, two
 * consumers. It was briefly exposed twice, as `lang` and an alias `locale`, which only invited the
 * question of how they differed. They didn't.
 */
const LocaleContext = createContext({
  lang: DEFAULT_LANGUAGE,
  t: (key) => key,
  eur: (n) => String(n),
  setLanguage: () => {},
});

export const LocaleProvider = ({ children }) => {
  // Start on the default for SSR and the first paint so the server-rendered markup matches what
  // React expects. The inline script in gatsby-ssr has already stamped <html lang>, and the effect
  // below reconciles to the stored choice without a second flash. Same dance ThemeProvider does.
  const [lang, setLang] = useState(DEFAULT_LANGUAGE);
  const { user } = useAuth();

  useEffect(() => {
    setLang(storedLanguage());
  }, []);

  /*
   * The server wins whenever it has spoken.
   *
   * `user.language` arrives from `/me` a moment after mount, and it is the value the 03:00 digest
   * will use — so if the two ever disagree, the local copy is the stale one. This is what makes a
   * language chosen on a phone show up on a laptop, and what stops a stale localStorage entry from
   * quietly overriding a preference the account actually holds.
   */
  useEffect(() => {
    const fromServer = user?.language?.toLowerCase();
    if (!fromServer || !LANGUAGES.includes(fromServer)) return;
    setLang(fromServer);
    storeLanguage(fromServer);
  }, [user?.language]);

  // Mirrored onto the document rather than kept in React alone: screen readers announce in the
  // language the element declares, and `lang` is what CSS `:lang()` and hyphenation read too.
  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  /**
   * Switch language locally and immediately.
   *
   * Persisting it to the account is the caller's job, not this one's — see `Profile.js`, which owns
   * the spinner and the rollback the way it already does for `emailPref`. Keeping the PATCH out of
   * here means the switch still works on a screen with no session behind it, and means this context
   * has no opinion about network failure.
   */
  const setLanguage = useCallback((next) => {
    if (!LANGUAGES.includes(next)) return;
    setLang(next);
    storeLanguage(next);

    /*
     * Google Maps is the one thing a re-render cannot re-language — see lib/mapsLanguage.js for
     * why a document reload is the only reliable way to do it.
     *
     * Reload here *only* if a map is on screen right now, where leaving an English map inside a
     * Greek page would be visibly wrong. Otherwise do nothing: the mismatch is recorded, and
     * `MapView` reloads on its next mount instead. That keeps the common case — switching in
     * Profile, which has no map — instant, and moves the reload onto a route change where it is
     * indistinguishable from ordinary navigation.
     */
    if (typeof window !== "undefined" && someMapIsMounted() && mapsLanguageIsStale()) {
      window.location.reload();
    }
  }, []);

  const value = useMemo(
    () => {
      const t = translator(lang);
      /*
       * An amount of euros, complete with symbol, placed where the language puts it.
       *
       * Two separate things vary and both were previously hard-coded as `€${n.toFixed(2)}`:
       * the decimal separator (Greek writes 2,47) and the symbol's position (Greek writes
       * "2,47 €", English "€2.47"). The separator comes from `Intl` via `money`, the position
       * from the `common.money` entry — so neither is decided in JSX.
       */
      const eur = (amount) => t("common.money", { amount: money(amount, lang) });
      return { lang, t, eur, setLanguage };
    },
    [lang, setLanguage]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = () => useContext(LocaleContext);
